import React, { useState } from "react";
import { usePledges } from "../../hooks/usePledges";
import ConfirmationModal from "../../common/ConfirmationModal";
import { TableStatus } from "../../components/TableStatus";
import PledgeModal from "./PledgeModal";
import CustomPagination from "../../common/Pagination";

export default function PledgeTablePage() {
    // 1. Setup Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // 2. Pass pagination state to the hook
    const {
        pledges,
        pagination,
        isLoading,
        isError,
        error,
        createPledge,
        updatePledge,
        deletePledge,
        isDeleting,
        isCreating,
        isUpdating,
    } = usePledges(currentPage, itemsPerPage);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPledge, setEditingPledge] = useState(null);
    const [pledgeToDelete, setPledgeToDelete] = useState(null);

    const handleOpenModal = (pledge = null) => {
        setEditingPledge(pledge);
        setIsModalOpen(true);
    };

    const handleModalSubmit = async (formData) => {
        try {
            if (editingPledge) {
                await updatePledge({ id: editingPledge._id, data: formData });
            } else {
                await createPledge(formData);
            }
            setIsModalOpen(false);
        } catch (error) {
            console.error("Failed to save pledge", error);
        }
    };

    const handleDelete = async () => {
        if (!pledgeToDelete) return;
        await deletePledge(pledgeToDelete._id);

        // 3. Prevent an empty page if you delete the last item on the current page
        if (pledges.length === 1 && currentPage > 1) {
            setCurrentPage((prev) => prev - 1);
        }

        setPledgeToDelete(null);
    };

    return (
        <>
            <div className="card shadow-sm">
                <div className="card-header bg-light d-flex justify-content-between align-items-center p-3">
                    <h4 className="mb-0 text-primary-emphasis">Pledge Management</h4>
                    <button
                        className="btn btn-success btn-sm"
                        onClick={() => handleOpenModal()}
                    >
                        <i className="fas fa-plus me-1"></i> Add Pledge
                    </button>
                </div>

                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th>No</th>
                                    <th>Icon</th>
                                    <th>Text</th> {/* ADDED HEADER HERE */}
                                    <th>Status</th>
                                    <th>Sort Order</th>
                                    <th className="text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <TableStatus
                                    status={isLoading ? "loading" : isError ? "failed" : "succeeded"}
                                    error={error}
                                    dataLength={pledges.length}
                                    colSpan={6}
                                    emptyText="No pledges found."
                                />

                                {!isLoading && !isError && pledges.map((item, index) => (
                                    <tr key={item._id}>
                                        {/* 4. Fix numbering for pagination */}
                                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                        <td>
                                            {item.icon ? (
                                                <img
                                                    src={item?.icon}
                                                    alt="icon"
                                                    width={150}
                                                    height={150}
                                                    className="rounded"
                                                    style={{ objectFit: "contain" }}
                                                />
                                            ) : (
                                                <span className="text-muted small">No Icon</span>
                                            )}
                                        </td>

                                        {/* ADDED TEXT COLUMN HERE */}
                                        <td>{item?.text}</td>

                                        <td>
                                            {item?.isActive ? (
                                                <span className="badge bg-success">Active</span>
                                            ) : (
                                                <span className="badge bg-secondary">Inactive</span>
                                            )}
                                        </td>
                                        <td>{item?.sort}</td>
                                        <td className="text-center">
                                            <div className="d-flex justify-content-center gap-2">
                                                <button
                                                    className="btn btn-sm btn-outline-primary mr-2"
                                                    onClick={() => handleOpenModal(item)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => setPledgeToDelete(item)}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 5. Render Pagination Component */}
                {pagination && pagination.totalPages > 1 && (
                    <div className="card-footer">
                        <CustomPagination
                            currentPage={currentPage}
                            totalPages={pagination.totalPages}
                            totalItems={pagination.totalRecords}
                            itemsPerPage={itemsPerPage}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                )}
            </div>

            <PledgeModal
                show={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleModalSubmit}
                initialData={editingPledge}
                isLoading={isCreating || isUpdating}
            />

            <ConfirmationModal
                show={pledgeToDelete !== null}
                onClose={() => setPledgeToDelete(null)}
                onConfirm={handleDelete}
                isLoading={isDeleting}
                title="Confirm Deletion"
                confirmText="Delete"
                confirmButtonVariant="danger"
            >
                Are you sure you want to delete this pledge?
            </ConfirmationModal>
        </>
    );
}